import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/AuthModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { normalizeProfileVertical } from '@photofeeler/shared';

export function Challenge() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const challengeData = useMemo(() => {
    // 1. Query-param format: /challenge/new?iq=85&context=dating&name=Alex
    const queryScore = parseInt(searchParams.get('iq') || '', 10);
    const queryContext = normalizeProfileVertical(searchParams.get('context'));
    const queryName = searchParams.get('name');

    if (!Number.isNaN(queryScore) && challengeId === 'new') {
      return {
        score: queryScore,
        context: queryContext,
        challengerName: queryName || 'Someone',
      };
    }

    // 2. iOS share format: /challenge/{score}-{context}-{name}
    //    e.g. /challenge/85-dating-Friend
    const sharePattern = /^(\d{1,3})-(dating|linkedin|social)-([\w%]+)$/i;
    const match = (challengeId || '').match(sharePattern);
    if (match) {
      return {
        score: parseInt(match[1], 10),
        context: normalizeProfileVertical(match[2]),
        challengerName: decodeURIComponent(match[3]) || 'Someone',
      };
    }

    // 3. Loose dash-separated fallback (legacy URLs)
    const parts = (challengeId || '').split('-');
    if (parts.length >= 2 && !Number.isNaN(parseInt(parts[0], 10))) {
      return {
        score: parseInt(parts[0], 10) || 0,
        context: normalizeProfileVertical(parts[1]),
        challengerName: decodeURIComponent(parts.slice(2).join('-') || 'Someone'),
      };
    }

    // 4. Opaque challenge ID — default presentation
    return {
      score: 0,
      context: normalizeProfileVertical(null),
      challengerName: 'Someone',
    };
  }, [challengeId, searchParams]);

  const contextLabel = challengeData.context === 'linkedin' ? 'LinkedIn' : 'Dating';
  usePageMetadata({
    title: `${contextLabel} Photo Challenge — PhotoFeeler AI`,
    description: `See if you can beat this ${contextLabel} photo score and start your own comparison for free.`,
    canonicalPath: challengeId ? `/challenge/${challengeId}` : '/challenge/new',
  });

  const openAuth = () => {
    if (isAuthenticated) {
      navigate(`/app?tab=new&vertical=${challengeData.context}`);
    } else {
      setAuthOpen(true);
    }
  };

  return (
    <div className="bg-cream min-h-screen relative overflow-hidden">
      <div className="page-orb left-[-5rem] top-24 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-4rem] top-32 h-72 w-72 bg-info/10" />
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        onEmailSignIn={signInWithEmail}
        onEmailSignUp={signUpWithEmail}
      />

      <div className="pt-28 pb-20 px-5 sm:px-8 max-w-lg mx-auto text-center animate-fade-in">
        <div className="page-hero-panel text-center mb-8">
          <div className="premium-kicker mb-3">Photo challenge</div>
          <h1 className="font-serif text-4xl text-ink mb-3" style={{ fontWeight: 600 }}>
            Beat this {contextLabel} score
          </h1>
          <p className="hero-copy max-w-md mx-auto">
            Start from the same premium audit flow and see whether your photo can outperform this benchmark.
          </p>
        </div>

        <div className="card-rose p-10 mb-8 lift-hover">
          <div className="text-[11px] font-sans text-ink-faint tracking-widest uppercase mb-2">Photo Challenge &middot; {contextLabel}</div>
          <p className="text-sm text-ink-muted mb-6" style={{ fontWeight: 300 }}>
            {challengeData.challengerName} scored
          </p>
          <div className="text-8xl font-sans text-emerald-600 mb-2" style={{ fontWeight: 600, letterSpacing: '-0.04em' }}>
            {challengeData.score}
          </div>
          <div className="text-sm font-sans text-ink" style={{ fontWeight: 500 }}>
            {challengeData.score >= 80 ? 'Excellent photo' : challengeData.score >= 60 ? 'Good photo' : 'Room to improve'}
          </div>
        </div>

        {/* CTA */}
        <h2 className="font-sans text-2xl text-ink mb-3" style={{ fontWeight: 600 }}>
          Think you can beat it?
        </h2>
        <p className="text-sm text-ink-faint mb-8" style={{ fontWeight: 300 }}>
          Upload your {contextLabel} photo and find out. 3 free credits, no card required.
        </p>

        <button onClick={openAuth} className="btn-rose text-sm w-full max-w-xs mx-auto">
          Upload your photo &mdash; it&apos;s free
        </button>

        <div className="mt-8 flex justify-center gap-8 text-center">
          {[
            ['3', 'AI models'],
            ['15s', 'analysis time'],
            ['Free', 'to try'],
          ].map(([num, label]) => (
            <div key={label} className="premium-stat min-w-[88px]">
              <div className="text-lg font-sans text-ink" style={{ fontWeight: 600 }}>{num}</div>
              <div className="text-[10px] text-ink-faint font-sans">{label}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-surface-border">
          <p className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
            Your photos are private and auto-deleted after 30 days. We rate the photo, not the person.
          </p>
        </div>
      </div>
    </div>
  );
}
